<template>
    <div class="sc-message">
        <div
            :class="{
                sent: message.author === 'me',
                received: message.author !== 'me' && message.type !== 'system',
                system: message.type === 'system',
                emoji: isEmoji,
            }"
            class="sc-message--content"
            :data-type="message.type"
        >
            <a-avatar
                v-if="
                    message.type !== 'system' &&
                    message.type !== 'private' &&
                    authorName !== undefined &&
                    authorName.length > 2
                "
                :style="{ backgroundColor: authorName === 'chatbot' ? '--color7' : getAvatarColor(authorName) }"
                :username="authorName"
                class="avatarIcon"
                :src="getGravatar"
            >
                <div v-if="authorName === 'chatbot'">
                    <Chatbot style="fill: var(--color7)" />
                </div>
                <div v-else>
                    {{ authorName.substr(0, 1).toUpperCase() }}
                </div>
            </a-avatar>
            <TextMessage
                v-if="message.type === 'text' || message.type === 'chatbot'"
                :message="message"
                :messageColors="determineMessageColors()"
                :messageStyling="messageStyling"
                @remove="$emit('remove')"
                @isEmoji="(emoji) => (isEmoji = emoji)"
            >
                <template v-slot:default="scopedProps">
                    <slot
                        :me="scopedProps.me"
                        :message="scopedProps.message"
                        :messageColors="scopedProps.messageColors"
                        :messageText="scopedProps.messageText"
                        name="text-message-body"
                    >
                    </slot>
                </template>
                <template v-slot:text-message-toolbox="scopedProps">
                    <slot
                        :me="scopedProps.me"
                        :message="scopedProps.message"
                        name="text-message-toolbox"
                        style="margin-bottom: 0 !important"
                    >
                    </slot>
                </template>
                <template #feedback> </template>
            </TextMessage>
            <VoiceMessage
                v-else-if="message.type === 'audio'"
                :message="message"
                :messageColors="determineMessageColors()"
            ></VoiceMessage>
            <VideoMessage v-else-if="isVideoMessage()" :message="message" />
            <AssetMessage v-else-if="message.type === 'asset'" :message="message"></AssetMessage>
            <EmojiMessage v-else-if="message.type === 'emoji'" :data="message.data"></EmojiMessage>
            <ImageMessage v-else-if="message.type === 'image'" :message="message"></ImageMessage>
            <FileMessage
                v-else-if="message.type === 'file'"
                :data="message.data"
                :messageColors="determineMessageColors()"
            >
            </FileMessage>

            <BotImageMessage v-else-if="message.type === 'chatbot-image'" :data="message" />
            <BotGalleryMessage
                v-else-if="message.type === 'chatbot-gallery'"
                :data="message"
                :disabled="conversationTerminated"
            />
            <BotButtonMessage
                v-else-if="message.type === 'chatbot-button' && message.raw.payload.type === 'BUTTON'"
                :data="message"
                :disabled="conversationTerminated"
            />
            <BotFileMessage v-else-if="message.type === 'chatbot-file'" :data="message" />
            <BotQuickReplyMessage
                v-else-if="message.type === 'chatbot-button' && message.raw.payload.type === 'QUICK_REPLY'"
                :data="message"
                :messageColors="determineMessageColors()"
                :messageStyling="messageStyling"
                :disabled="conversationTerminated"
            />
            <GetLocationMessage
                v-else-if="message.type === 'chatbot-get-location'"
                :message="message"
                :disabled="conversationTerminated"
            />

            <TypingMessage v-else-if="message.type === 'typing'" :messageColors="determineMessageColors()" />
            <SystemMessage
                v-else-if="message.type === 'system' && shouldShowSystem"
                :data="message.data"
                :messageColors="determineMessageColors()"
            >
                <slot :message="message.data" name="system-message-body"></slot>
            </SystemMessage>
            <audio v-else-if="message.type === 'audio'" controls>
                <source src="https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3" type="audio/mpeg" />
                Your browser does not support the audio element.
            </audio>
            <LocationMessage v-else-if="message.type === 'location'" :message="message" />
            <a-divider v-else-if="message.type === 'divider'" class="daysDivider" style="color: var(--color7)">
                {{ message.date }}
            </a-divider>
        </div>
    </div>
</template>

<script>
import TextMessage from "./messages/TextMessage.vue";
import FileMessage from "./messages/FileMessage.vue";
import EmojiMessage from "./messages/EmojiMessage.vue";
import TypingMessage from "./messages/TypingMessage.vue";
import SystemMessage from "./messages/SystemMessage.vue";
import VoiceMessage from "./messages/VoiceMessage.vue";
import Chatbot from "../../svg/Chatbot";
import * as _ from "lodash";
import { log2 } from "../../../utils/log2";
import ImageMessage from "./messages/ImageMessage";
import VideoMessage from "./messages/VideoMessage.vue";
import AssetMessage from "./messages/AssetMessage";
import LocationMessage from "./messages/LocationMessage";
import BotQuickReplyMessage from "./messages/chatbot/BotQuickReplyMessage";
import BotButtonMessage from "./messages/chatbot/BotButtonMessage";
import BotGalleryMessage from "./messages/chatbot/BotGalleryMessage";
import BotImageMessage from "./messages/chatbot/BotImageMessage";
import { mapState } from "vuex";
import GetLocationMessage from "./messages/chatbot/GetLocationMessage";
import BotFileMessage from "./messages/chatbot/BotFileMessage.vue";

const log = log2("Message");
export default {
    data() {
        return {
            isEmoji: false,
            avatarColors: ["#3699FF", "#dc3545", "#607d8b", "#4caf50", "#12078a"],
        };
    },
    components: {
        BotFileMessage,
        BotQuickReplyMessage,
        BotButtonMessage,
        BotGalleryMessage,
        BotImageMessage,
        GetLocationMessage,
        LocationMessage,
        TextMessage,
        FileMessage,
        EmojiMessage,
        TypingMessage,
        SystemMessage,
        VoiceMessage,
        Chatbot,
        ImageMessage,
        VideoMessage,
        AssetMessage,
    },
    props: {
        message: {
            type: Object,
            required: true,
        },
        lastMessage: {
            type: Object,
        },
        colors: {
            type: Object,
            required: true,
        },
        messageStyling: {
            type: Boolean,
            required: true,
        },
        user: {
            type: Object,
            required: true,
        },
        participants: {
            type: Array,
            default: () => [],
        },
        conversationTerminated: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    methods: {
        isVideoMessage() {
            const mime = _.get(this.message, "raw.asset.mime", "") || "";
            const ext = (_.get(this.message, "raw.asset.ext", "") || "").toLowerCase();
            const videoExts = ["mp4", "webm", "mov", "avi", "mkv", "m4v"];
            const isVideoByMime = mime.includes("video");
            const isVideoByExt = videoExts.includes(ext);
            const isVideoType = this.message.type === "video";
            return isVideoType || isVideoByMime || isVideoByExt;
        },
        getAvatarColor(name = "") {
            let hashCode = _.sum(name.split("").map((x) => x.charCodeAt(0)));
            const color = this.avatarColors[hashCode % this.avatarColors.length];
            return color;
        },
        sentColorsStyle() {
            return {
                color: this.colors.sentMessage.text,
                backgroundColor: this.colors.sentMessage.bg,
            };
        },
        receivedColorsStyle() {
            return {
                color: this.colors.receivedMessage.text,
                backgroundColor: this.colors.receivedMessage.bg,
            };
        },
        determineMessageColors() {
            return this.message.author === "me" ? this.sentColorsStyle() : this.receivedColorsStyle();
        },
    },

    computed: {
        ...mapState({
            settings: (state) => state.setting.settings,
        }),
        shouldShowSystem() {
            const agentMsgList = ["user-login", "user-destroy", "user-leave"];
            return (
                !agentMsgList.includes(this.message.raw.systemMessageType) || !this.settings.disableAgentSystemMessages
            );
        },

        authorName() {
            if (_.get(this.message, "raw.username")) {
                return this.message.raw.username;
            } else if (this.message.author === "chatbot") {
                return "chatbot";
            } else {
                return "";
            }
        },
        chatImageUrl() {
            return (this.user && this.user.imageUrl) || this.chatIcon;
        },
        getGravatar() {
            let maybeUser = this.participants.find(
                (x) => x._id === this.message.raw.user || x._id === this.message.author
            );
            if (maybeUser) {
                return maybeUser.gravatar || undefined;
            }
            return undefined;
        },
    },
    mounted() {
        //log("Message component mount", this.message)
    },
};
</script>
<style lang="scss">
.avatarIcon {
    width: 35px !important;
    height: 35px !important;
    min-width: 35px !important;
    min-height: 35px !important;
    line-height: 40px !important;
    font-size: 15px !important;
    color: white !important;
    display: flex !important;
    align-items: center !important;
    margin-right: 5px !important;
    overflow: hidden !important;
}

.sc-message {
    /*width: 300px;
      margin: auto;*/
    /* Balonlar aniden bitiveriyordu; Jivo'da ~250ms opaklikla beliriyorlar. */
    animation: sps-message-in 0.25s cubic-bezier(0.22, 0.61, 0.36, 1) both;
    padding-bottom: 10px;
    width: 100%;
    display: flex;
    justify-content: right;
    //padding-right: 10px;
    //padding-left: 10px;

    .sc-message--edited {
        opacity: 0.7;
        word-wrap: normal;
        font-size: xx-small;
        text-align: center;
    }
}

.sc-message--content {
    width: 100%;
    display: flex;

    &[data-type="chatbot-gallery"] {
        overflow: hidden;
        position: relative;
    }
}

.sc-message--content.sent {
    justify-content: flex-end;
}

.sc-message--content.system {
    justify-content: center;

    span {
        word-break: normal !important;
    }
}

.sc-message--content.sent .sc-message--avatar {
    display: none;
}

.sc-message--avatar {
    background-repeat: no-repeat;
    background-size: 100%;
    background-position: center;
    min-width: 30px;
    min-height: 30px;
    border-radius: 50%;
    align-self: center;
    margin-right: 15px;
}

.sc-message--meta {
    font-size: xx-small;
    margin-bottom: 0px;
    color: gray !important;
    text-align: center;
    font-weight: 100;
    position: absolute;
    right: 6px;
    bottom: 2px;
}

@media (max-width: 450px) {
    .sc-message {
        .span {
            word-break: normal !important;
        }
    }
}

.sc-message--text {
    box-shadow: 0px 1px 2px 1px #0000001c;
    padding: 5px 30px 17px 15px;
    font-size: 14px;
    line-height: 1.4;
    position: relative;
    -webkit-font-smoothing: subpixel-antialiased;
    border-radius: var(--sps-radius-md);

    .sc-message--text-content {
        p:last-child {
            margin: 0 !important;
        }
    }

    &:hover .sc-message--toolbox {
        left: -20px;
        opacity: 1;
        display: none;
    }

    .sc-message--toolbox {
        transition: left 0.2s ease-out 0s;
        white-space: normal;
        opacity: 0;
        position: absolute;
        left: 0px;
        width: 25px;
        top: 0;
        display: none;

        button {
            background: none;
            border: none;
            padding: 0px;
            margin: 0px;
            outline: none;
            width: 100%;
            text-align: center;
            cursor: pointer;

            &:focus {
                outline: none;
            }
        }
    }
}

.received {
    .sc-message--text {
        background-color: var(--chat-bubble-background, #ffffff) !important;
        color: var(--chat-bubble-text, #000000) !important;
    }
}

.sc-message--content.sent .sc-message--text {
    color: white;
    background-color: #4e8cff;
    max-width: calc(100% - 120px);
    text-overflow: clip !important;
    word-break: break-word !important;
}

.sc-message--text code {
    font-family: "Courier New", Courier, monospace !important;
}
.sc-message--content.received .sc-message--text {
    color: #263238;
    background-color: #f4f7f9;
    margin: 0 !important;
    max-width: calc(100% - 60px);
    text-overflow: clip !important;
    word-break: break-word !important;
}

.sc-message--content.sent .videoMessage {
    max-width: calc(100% - 120px);
}

.sc-message--content.received .videoMessage {
    max-width: calc(100% - 60px);
}

.tooltip {
    display: block !important;
    z-index: 10000;

    .tooltip-inner {
        background: black;
        color: white;
        border-radius: var(--sps-radius-md);
        padding: 5px 10px 4px;
    }

    .tooltip-arrow {
        width: 0;
        height: 0;
        border-style: solid;
        position: absolute;
        margin: 5px;
        border-color: black;
        z-index: 1;
    }

    &[x-placement^="top"] {
        margin-bottom: 5px;

        .tooltip-arrow {
            border-width: 5px 5px 0 5px;
            border-left-color: transparent !important;
            border-right-color: transparent !important;
            border-bottom-color: transparent !important;
            bottom: -5px;
            left: calc(50% - 5px);
            margin-top: 0;
            margin-bottom: 0;
        }
    }

    &[x-placement^="bottom"] {
        margin-top: 5px;

        .tooltip-arrow {
            border-width: 0 5px 5px 5px;
            border-left-color: transparent !important;
            border-right-color: transparent !important;
            border-top-color: transparent !important;
            top: -5px;
            left: calc(50% - 5px);
            margin-top: 0;
            margin-bottom: 0;
        }
    }

    &[x-placement^="right"] {
        margin-left: 5px;

        .tooltip-arrow {
            border-width: 5px 5px 5px 0;
            border-left-color: transparent !important;
            border-top-color: transparent !important;
            border-bottom-color: transparent !important;
            left: -5px;
            top: calc(50% - 5px);
            margin-left: 0;
            margin-right: 0;
        }
    }

    &[x-placement^="left"] {
        margin-right: 5px;

        .tooltip-arrow {
            border-width: 5px 0 5px 5px;
            border-top-color: transparent !important;
            border-right-color: transparent !important;
            border-bottom-color: transparent !important;
            right: -5px;
            top: calc(50% - 5px);
            margin-left: 0;
            margin-right: 0;
        }
    }

    &[aria-hidden="true"] {
        visibility: hidden;
        opacity: 0;
        transition: opacity 0.15s, visibility 0.15s;
    }

    &[aria-hidden="false"] {
        visibility: visible;
        opacity: 1;
        transition: opacity 0.15s;
    }

    &.info {
        $color: rgba(#004499, 0.9);

        .tooltip-inner {
            background: $color;
            color: white;
            padding: 24px;
            border-radius: var(--sps-radius-sm);
            box-shadow: 0 5px 30px rgba(black, 0.1);
        }

        .tooltip-arrow {
            border-color: $color;
        }
    }

    &.popover {
        $color: #f9f9f9;

        .popover-inner {
            background: $color;
            color: black;
            padding: 24px;
            border-radius: var(--sps-radius-sm);
            box-shadow: 0 5px 30px rgba(black, 0.1);
        }

        .popover-arrow {
            border-color: $color;
        }
    }
}

.sc-message--content {
    .wrong-feedback {
        border-radius: var(--sps-radius-md);
        position: absolute;
        bottom: 2.3rem;
        right: -0.1rem;
        transition: all 0.3s;
        opacity: 0;
        cursor: pointer;

        i {
            color: red;
        }
    }

    &:hover {
        .wrong-feedback {
            opacity: 1;
        }
    }
}
@keyframes sps-message-in {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@media (prefers-reduced-motion: reduce) {
    .sc-message {
        animation: none;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/V2/popup-chat/Message.vue